import {
    Children,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Box,
    IconButton,
    Typography,
} from "@mui/material";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import { Swiper } from "swiper/react";

import "swiper/css";


const formatSlideNumber = (number) => {
    return String(number).padStart(2, "0");
};


const ReimoSwiper = ({
    children,
    height = "400px",
    slidesPerView = 1,
    spaceBetween = 30,
    loop = false,
    showNavigation = true,
    navigationPosition = "bottom",
    resetOnChildrenChange = true,
    swiperProps = {},
    sx = {},
}) => {
    const swiperRef = useRef(null);

    const [activeSlide, setActiveSlide] = useState(0);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
	const [currentSlidesPerView, setCurrentSlidesPerView] = useState(
	    typeof slidesPerView === "number" ? slidesPerView : 1
	);
    /*
     * Children.toArray entfernt unter anderem null/false-Werte
     * und liefert uns eine stabile Liste der tatsächlich
     * übergebenen React-Children.
     */
    const slides = useMemo(() => {
        return Children.toArray(children);
    }, [children]);

    const slideCount = slides.length;

    /*
     * Bei mehreren sichtbaren Slides muss berücksichtigt werden,
     * dass eventuell gar nicht gescrollt werden kann.
     */
    const canNavigate = slideCount > currentSlidesPerView;

    const updateSwiperState = (swiper) => {
		const currentSlides =
		    typeof swiper.params.slidesPerView === "number"
		        ? swiper.params.slidesPerView
		        : 1;
		
		setCurrentSlidesPerView(currentSlides);
        if (!swiper || swiper.destroyed) {
            return;
        }

        const currentIndex = loop
            ? swiper.realIndex
            : swiper.activeIndex;

        setActiveSlide(currentIndex);
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };

    const handleSwiperInit = (swiper) => {
        swiperRef.current = swiper;

        /*
         * Direkt nach der Initialisierung Swiper einmal vollständig
         * berechnen lassen.
         */
        swiper.update();
        updateSwiperState(swiper);
    };

    const handleSlideChange = (swiper) => {
        updateSwiperState(swiper);
    };

    const handlePrevious = () => {
        const swiper = swiperRef.current;

        if (!swiper || swiper.destroyed) {
            return;
        }

        swiper.slidePrev();
    };

    const handleNext = () => {
        const swiper = swiperRef.current;

        if (!swiper || swiper.destroyed) {
            return;
        }

        swiper.slideNext();
    };

    /*
     * Dieser Effect läuft, sobald sich die Anzahl der Slides ändert,
     * beispielsweise nach Auswahl eines Filters.
     */
    useEffect(() => {
        const swiper = swiperRef.current;

        if (!swiper || swiper.destroyed) {
            return;
        }

        /*
         * React muss die neuen SwiperSlides zunächst in das DOM
         * geschrieben haben. requestAnimationFrame verschiebt das
         * Update auf den nächsten Browser-Renderzyklus.
         */
        const animationFrame = window.requestAnimationFrame(() => {
            if (!swiper || swiper.destroyed) {
                return;
            }

            swiper.update();

            if (resetOnChildrenChange && slideCount > 0) {
                /*
                 * Bei loop=false auf den ersten Slide.
                 * Geschwindigkeit 0 verhindert eine sichtbare Animation.
                 */
                if (loop) {
                    swiper.slideToLoop(0, 0, false);
                } else {
                    swiper.slideTo(0, 0, false);
                }
            }

            /*
             * Noch einmal aktualisieren, da slideTo den Status von
             * isBeginning und isEnd verändert.
             */
            swiper.update();
            updateSwiperState(swiper);
        });

        return () => {
            window.cancelAnimationFrame(animationFrame);
        };
    }, [
        slideCount,
        loop,
        resetOnChildrenChange,
    ]);

    /*
     * Wenn keine Slides vorhanden sind, setzen wir auch den
     * React-Zustand zurück.
     */
    useEffect(() => {
        if (slideCount === 0) {
            setActiveSlide(0);
            setIsBeginning(true);
            setIsEnd(true);
        }
    }, [slideCount]);

    const navigation = (
        showNavigation &&
        canNavigate && (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: {
                        xs: "18px",
                        sm: "32px",
                    },

                    mt:
                        navigationPosition === "bottom"
                            ? "24px"
                            : 0,

                    mb:
                        navigationPosition === "top"
                            ? "24px"
                            : 0,
                }}
            >
                <IconButton
                    type="button"
                    aria-label="Vorherige Folie"
                    onClick={handlePrevious}
                    disabled={!loop && isBeginning}
                    sx={{
	                    width: {
	                        xs: "30px",
	                        sm: "34px",
	                    },
	                    height: {
	                        xs: "30px",
	                        sm: "34px",
	                    },

                        /*
                         * Hier deine bereits angepassten Farben einsetzen.
                         */
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",

                        "&:hover": {
                            backgroundColor: "primary.dark",
                        },

                        "&.Mui-disabled": {
                            backgroundColor: "action.disabledBackground",
                            color: "action.disabled",
                        },
                    }}
                >
                    <ArrowBackIosNewIcon
                        sx={{
                            fontSize: {
                                xs: "24px",
                                sm: "30px",
                            },
                        }}
                    />
                </IconButton>

{currentSlidesPerView <= 1 ? (
    <>
        {formatSlideNumber(
            Math.min(activeSlide + 1, slideCount)
        )}

        <Box
            component="span"
            sx={{
                mx: {
                    xs: "10px",
                    sm: "14px",
                },
                fontWeight: 600,
            }}
        >
            /
        </Box>

        {formatSlideNumber(slideCount)}
    </>
) : (
    (() => {
        const start = activeSlide + 1;
        const end = Math.min(
            activeSlide + currentSlidesPerView,
            slideCount
        );

        return start === end
            ? `${start} von ${slideCount}`
            : `${start}–${end} von ${slideCount}`;
    })()
)}
                <IconButton
                    type="button"
                    aria-label="Nächste Folie"
                    onClick={handleNext}
                    disabled={!loop && isEnd}
                    sx={{
	                    width: {
	                        xs: "30px",
	                        sm: "34px",
	                    },
	                    height: {
	                        xs: "30px",
	                        sm: "34px",
	                    },
					    bgcolor: "primary.main",
					    color: "primary.contrastText",                    
	
	
	                    "&:hover": {
							bgcolor: "primary.dark",
	                    },
	
                        "&.Mui-disabled": {
                            backgroundColor: "action.disabledBackground",
                            color: "action.disabled",
                        },
                    }}
                >
                    <ArrowForwardIosIcon
                        sx={{
                            fontSize: {
                                xs: "24px",
                                sm: "30px",
                            },
                        }}
                    />
                </IconButton>
            </Box>
        )
    );

    return (
        <Box sx={sx}>
            {navigationPosition === "top" && navigation}

            <Swiper
                style={{
                    height,
                }}
                slidesPerView={slidesPerView}
                spaceBetween={spaceBetween}
                loop={loop}
                onSwiper={handleSwiperInit}
                onSlideChange={handleSlideChange}
                onReachBeginning={updateSwiperState}
                onReachEnd={updateSwiperState}
                onFromEdge={updateSwiperState}
                observer={true}
                observeParents={true}
                {...swiperProps}
            >
                {slides}
            </Swiper>

            {navigationPosition === "bottom" && navigation}
        </Box>
    );
};

export default ReimoSwiper;



